/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 */

import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {IxModule, ToastService} from '@siemens/ix-angular';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {DppService} from '../../../../services/dpp.service';
import {Submodel} from '@product-passport/dpp/model';
import {getSubmodelMetadata} from '../../../../models/dpp/submodel-types';
import {DppSubmodelFieldComponent} from '../dpp-submodel-field/dpp-submodel-field.component';
import {WindowSizeService} from '../../../../services/window-size.service';
import {LocalizationHelper} from '../../../shared/utils/localization-helper';

@Component({
  selector: 'app-generic-submodel-viewer',
  templateUrl: './generic-submodel-viewer.component.html',
  styleUrl: './generic-submodel-viewer.component.scss',
  imports: [IxModule, TranslateModule, DppSubmodelFieldComponent],
})
export class GenericSubmodelViewerComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dppService = inject(DppService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly localizationHelper = inject(LocalizationHelper);

  private routeSubscription?: Subscription;

  dppId = '';
  submodelId = '';
  shellName = '';

  submodel?: Submodel;

  loading = true;
  passFound = true;

  get submodelVersion(): string | undefined {
    const semanticId = this.submodel?.semanticId?.keys?.[0]?.value;
    return getSubmodelMetadata(semanticId)?.version;
  }

  get submodelName(): string {
    const semanticId = this.submodel?.semanticId?.keys?.[0]?.value;
    const metadata = getSubmodelMetadata(semanticId);
    if (metadata) {
      return this.translate.instant(metadata.type);
    }
    return this.localizationHelper.getLocalizedText(
      this.submodel?.displayName,
      this.submodel?.idShort ?? this.submodelId,
    );
  }

  protected readonly WindowSizeService = WindowSizeService;

  ngOnInit(): void {
    this.routeSubscription = this.route.params.subscribe(async (params) => {
      this.dppId = params['aasId'];
      this.submodelId = params['submodelId'];
      await this.loadSubmodelData();
    });
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
  }

  private async loadSubmodelData(): Promise<void> {
    try {
      this.submodel = await this.dppService.getSubmodel(this.submodelId);
      this.passFound = true;
    } catch {
      this.passFound = false;
      await this.toastService.show({
        message: this.translate.instant(
          'dpp.passport_detail_view.load_error_message',
        ),
        type: 'error',
      });
    } finally {
      this.loading = false;
    }

    this.dppService
      .getDppShell(this.dppId)
      .then((shell) => {
        this.shellName = this.localizationHelper.getLocalizedText(
          shell.displayName,
          this.dppId,
        );
      })
      .catch(() => {
        this.shellName = this.dppId;
      });
  }

  onBackButtonClick(): void {
    this.navigateToPassportDetail();
  }

  navigateToPassportList(): void {
    this.router.navigate(['/app/dpp/list-passports']);
  }

  navigateToPassportDetail(): void {
    this.router.navigate(['/app/dpp/view-passport', this.dppId]);
  }

  navigateToGenericEditor(): void {
    this.router.navigate(
      [
        '/app/dpp/view-passport',
        this.dppId,
        'submodel',
        this.submodelId,
        'edit',
      ],
      {queryParams: {shellName: this.shellName}},
    );
  }
}
